import React from 'react';
import { gql, useMutation } from '@apollo/client';
import Modal from 'styled-react-modal';
import styled, { useTheme } from 'styled-components';
import { labelFadeTransition } from '../styles/motion';
import { FormattedMessage } from 'react-intl';
import { Wrapper } from '../StyledComponents';
import { Button, Input } from '../StyledComponents';
import Spinner from './Spinner';
import { toEnglishAuthError } from '../auth/authErrorMessage';

const StyledModal = Modal.styled`
  margin-left: auto;
  margin-right: auto;
  margin-top: 3em;
  background: ${({ theme }) => theme.colors.cardBg};
  border-radius: 18px;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.18);
  border: none;
  padding: 2.5em 2em 2em 2em;
  width: 95vw;
  max-width: 420px;
  transition: box-shadow 0.2s;
  @media(min-width: 960px) {
    width: 420px;
  }
`;

const Row = styled.div`
  position: relative;
  margin-top: 1.2em;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 340px;
`;

const FormButton = styled(Button)`
  font-family: NeueEinstellungBold;
  text-align: center;
  white-space: nowrap;
  line-height: 1.5;
  font-size: 1.15rem;
  background: linear-gradient(90deg, #8b7d65 0%, #bcae8a 100%);
  border: none;
  color: #fff;
  outline: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(139, 125, 101, 0.08);
  padding: 0.9em 0;
  margin-bottom: 0.5em;
  letter-spacing: 0.01em;
  transition: background 0.2s, box-shadow 0.2s;
  cursor: pointer;
  min-height: 48px;
  &:hover:not(:disabled), &:focus:not(:disabled) {
    background: linear-gradient(90deg, #bcae8a 0%, #8b7d65 100%);
    box-shadow: 0 4px 16px rgba(139, 125, 101, 0.13);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(FormButton)`
  background: ${({ theme }) => theme.colors.cardBg};
  color: ${({ theme }) => theme.colors.sectionAccent};
  border: 1.5px solid ${({ theme }) => theme.colors.sectionAccent};
  box-shadow: 0 2px 8px rgba(139, 125, 101, 0.06);
  &:hover:not(:disabled), &:focus:not(:disabled) {
    background: ${({ theme }) => theme.colors.surfaceHover};
    box-shadow: 0 2px 12px rgba(139, 125, 101, 0.1);
  }
`;

const FormInput = styled(Input)`
  font-family: NeueEinstellungRegular;
  padding: 1.1em 1em 1.1em 1em;
  width: 100%;
  outline: 0px;
  border: 1.5px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: 7px;
  font-size: 1.1rem;
  appearance: none;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.inputBg};
  color: ${({ theme }) => theme.colors.inputText};
  margin-bottom: 0.2em;
  transition: border 0.2s, box-shadow 0.2s;
  &:focus {
    border: 1.5px solid ${({ theme }) => theme.colors.sectionAccent};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const FormLabel = styled.label<{ $hide?: boolean }>`
  position: absolute;
  left: 1.2em;
  top: 1.1em;
  font-size: 0.93rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: NeueEinstellungRegular;
  background: transparent;
  line-height: 1.5;
  pointer-events: none;
  ${labelFadeTransition}
  z-index: 2;
  opacity: ${({ $hide }) => ($hide ? 0 : 1)};
`;

const ErrorDiv = styled.div`
  font-family: NeueEinstellungRegular;
  font-size: 1rem;
  line-height: 1.3;
  padding: 0.9em 1em;
  position: relative;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.error};
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: 7px;
  box-shadow: 0 2px 8px rgba(219, 41, 41, 0.08);
  text-align: center;
`;

const SuccessMessage = styled.p`
  font-family: NeueEinstellungRegular;
  font-size: 1.05rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0 0 1.2em 0;
  padding: 1em 0.5em;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const REQUEST_PASSWORD_RESET = gql`
  mutation requestPasswordReset($email: String!) {
    requestPasswordReset(email: $email)
  }
`;

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ForgotPasswordModal({ isOpen, onClose }: ForgotPasswordModalProps) {
  const theme = useTheme();
  const [email, setEmail] = React.useState('');
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [requestReset, { loading }] = useMutation(REQUEST_PASSWORD_RESET, {
    onCompleted: () => setSent(true),
    onError: (e) => setError(toEnglishAuthError(e.message)),
  });

  return (
    <StyledModal
      isOpen={isOpen}
      onEscapeKeydown={onClose}
      onBackgroundClick={onClose}
      contentLabel="Forgot Password"
    >
      <Wrapper>
        <h1
          className="text-center"
          style={{
            fontFamily: 'NeueEinstellungBold',
            fontSize: '2.1rem',
            margin: '0.2em 0 0.7em 0',
            color: theme.colors.sectionAccent,
            letterSpacing: '0.01em',
          }}
        >
          <FormattedMessage id="login.forgot_password" defaultMessage="Forgot Password?" />
        </h1>

        {sent ? (
          <>
            <SuccessMessage>
              <FormattedMessage
                id="login.reset_email_sent"
                defaultMessage="If the email exists, a reset link has been sent."
              />
            </SuccessMessage>
            <Row>
              <SecondaryButton onClick={onClose}>
                <FormattedMessage id="login.back_to_login" defaultMessage="Back to Login" />
              </SecondaryButton>
            </Row>
          </>
        ) : (
          <>
            {error && (
              <Row>
                <ErrorDiv>{error}</ErrorDiv>
              </Row>
            )}
            <Row>
              <FormInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=""
                autoComplete="email"
              />
              <FormLabel $hide={!!email}>
                <FormattedMessage id="signup.email" defaultMessage="E-mail" />
              </FormLabel>
            </Row>
            <Row>
              <FormButton
                onClick={() => requestReset({ variables: { email } })}
                disabled={loading || !email.trim()}
              >
                {loading ? (
                  <Spinner />
                ) : (
                  <FormattedMessage id="login.send_reset" defaultMessage="Send Reset Link" />
                )}
              </FormButton>
            </Row>
            <Row>
              <SecondaryButton onClick={onClose}>
                <FormattedMessage id="login.cancel" defaultMessage="Cancel" />
              </SecondaryButton>
            </Row>
          </>
        )}
      </Wrapper>
    </StyledModal>
  );
}
